import { LabelBindingModel, Platform, ResourceType } from './common';
import { BaseViolationRule } from './governance/violationRule.model';
import {
  InventoryAdditionalProperties,
  InventoryEntityField,
  InventoryPlatformUrls,
  InventoryResourceErrorsInfo,
  InventoryResourceIdentities,
  InventoryResourcePropertiesBase,
  InventoryResourceState,
  ResourceCategory,
  ResourceStatus,
} from './inventoryResource.model';
import { JsonHalResource } from './jsonhal.model';

/**
 * An enum representing the connectors categories used on the dlp policies.
 */
export enum ConnectorCategories {
  onPrem = 'on_prem',
  cloudBusinessData = 'cloud_business_data',
  unmanaged = 'unmanaged_cloud_endpoint',
  socialMedia = 'social_media_platform',
  highRisk = 'high_risk_connectors',
  communications = 'communication_systems',
}

/**
 * An enum representing violation status
 */
export enum ViolationStatus {
  open = 'open',
  resolved = 'resolved',
  exempted = 'exempted',
}

/**
 * An enum representing violation status reason
 */
export enum ViolationStatusReason {
  openedByZenity = 'openedByZenity',
  reopenedByZenity = 'reopenedByZenity',
  exemptedByUser = 'exemptedByUser',
  fixed = 'fixed',
  resourceDeleted = 'resourceDeleted',
  policyChangedByUser = 'policyChangedByUser',
  ruleUpdatedByZenity = 'ruleUpdatedByZenity',
  expired = 'expired',
}

/**
 * An enum representing the statuses that can be use as part of the update
 */
export enum UpdatableViolationStatus {
  exempted = 'exempted',
}

export interface ViolationResourcePlatformInfo {
  resourceId: string;
  platform: Platform;
  urls: InventoryEntityField<InventoryPlatformUrls>;
}

export interface ViolationResourceResourceState {
  status: ResourceStatus;
}

export interface ViolationResourceEnvironment extends JsonHalResource {
  id: string;
  displayName: string;
  environmentType: ResourceType;
}
export interface ViolationResource extends JsonHalResource {
  id: string;
  displayName: string;
  resourceCategory: ResourceCategory;
  resourceType: ResourceType;
  resourceState?: InventoryResourceState;
  platformInfo: ViolationResourcePlatformInfo;
  environment?: ViolationResourceEnvironment;
  identities?: InventoryResourceIdentities;
  resourceCategoryProperties?: InventoryResourcePropertiesBase;
  errorsInfo?: InventoryResourceErrorsInfo;
  resourceAdditionalProperties?: InventoryAdditionalProperties;
}

interface IntegrationInViolation {
  id: string;
}

interface PolicyInViolation {
  id: string;
}

export interface ViolationNote {
  id: string;
  content: string;
  addedBy: string;
  addedAt: Date;
}

export interface ViolationAudit extends JsonHalResource {
  id: string;
  message: string;
  userIdentity: string;
  eventTime: Date;
}

export interface ViolationAddNote {
  content: string;
}

export interface BindLabelToViolation {
  labelId: string;
  description: string;
}

/**
 * An enum representing the violations data type
 */
export enum ViolationsDataType {
  violations = 'violations',
  hygiene = 'hygiene',
}

export interface ViolationResolutionInfo {
  resolvedSince: Date;
}

/**
 * A class that represents a single vulnerability on a resource in a specific scan
 */

export enum UpdatableStatusesApi {}
export class UpdateViolationsStatusApiModel {
  public ids: string[];
  public status: UpdatableViolationStatus;
  public change_reason: ViolationChangeJustificationApiModel;
}

export class ResetViolationsStatusApiModel {
  public ids: string[];
  public reset_reason: ViolationChangeJustificationApiModel;
}

export class ViolationChangeJustificationApiModel {
  public justification: string;
}

export class ViolationsStatusUpdateResponse {
  public existingIds: string[];
  public notFoundIds: string[];
  public updatedIdsFromExistingAmount: string[];
}

export class BulkActionSubmitResult {
  public totalMatchedResultsCount: string;
  public resultsToApplyActionCount: string;
  public resultsNotApplicableCount: number;
  public requestId: string;
}

export class ViolationsActionBulkApiModel {
  public dataType: ViolationsDataType;
  public change_reason: ViolationChangeJustificationApiModel;
}

export class ViolationModel {
  public id: string;
  public zenityAccountId: string;
  public integration: IntegrationInViolation;
  public rule: BaseViolationRule;
  public resource: ViolationResource;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public findings: { [key: string]: any }[];
  public firstSeen: Date;
  public lastSeen?: Date;
  public status: ViolationStatus;
  public statusSince?: Date;
  public statusReason: ViolationStatusReason;
  public labelBindings: LabelBindingModel[];
  public policy: PolicyInViolation;
}
